import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addToast } from '@/store/app/action';
import ModalBasic from '@/components/ModalBasic';
import {
  saveMarketplaceColumnMapping,
  deleteMarketplaceColumnMapping,
  createMarketplaceTransformStep,
  updateMarketplaceTransformStep,
  deleteMarketplaceTransformStep,
  setMarketplaceTransformStepEnabled,
  reorderMarketplaceTransformSteps,
  createMarketplaceSource,
  updateMarketplaceSource,
  deleteMarketplaceSource,
  setMarketplaceSourceTrigger,
} from '@/api';
import ExecutionPathBanner from './ExecutionPathBanner';
import ColumnMappingCard from './ColumnMappingCard';
import DeclaredInputsCard from './DeclaredInputsCard';
import SourceEditorModal from './SourceEditorModal';
import TransformStepsList from './TransformStepsList';
import StepEditorModal from './StepEditorModal';
import TransformTestPanel from './TransformTestPanel';
import { apiErrorMessage, isMultiFile, triggerSource } from './constants';

/**
 * One marketplace store's feed configuration.
 *
 * Two things sit behind the word "rule" here, and the distinction is the whole
 * point of the view: the column mapping carries CSV header names only, while the
 * transform steps carry the logic imported from the store's retired Glue job.
 * A mapping can report itself perfectly healthy while the logic that used to
 * round prices or floor quantities is simply gone — which is why the execution
 * path and the per-step importer notes lead the page.
 */
const MarketplaceFeedRules = ({ store, stepsSupported, sourcesSupported, onRefresh }) => {
  const dispatch = useDispatch();

  const [busy, setBusy] = useState(false);
  const [stepEditor, setStepEditor] = useState({ open: false, step: null });
  const [deletingStep, setDeletingStep] = useState(null);
  const [sourceEditor, setSourceEditor] = useState({ open: false, source: null });
  const [deletingSource, setDeletingSource] = useState(null);

  const multiFile = isMultiFile(store);
  const trigger = triggerSource(store);

  const toast = (type, message) => dispatch(addToast({ type, message, timer: 6 }));

  // Every write returns the store-level warnings the change produced, so surface
  // those instead of a bare "saved" — disabling one step silently changes what
  // the pipeline does with every subsequent file.
  const reportWarnings = (warnings) => {
    (warnings || []).forEach((warning) => toast('warning', warning));
  };

  const run = async (action, successMessage, failureMessage) => {
    setBusy(true);
    try {
      const res = await action();
      reportWarnings(res?.warnings);
      if (successMessage) toast('success', successMessage);
      await onRefresh();
      return true;
    } catch (error) {
      console.error(failureMessage, error);
      toast('error', apiErrorMessage(error, failureMessage));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveMapping = (draft) =>
    run(
      () => saveMarketplaceColumnMapping(store.storeId, draft),
      'Column mapping saved and pinned as manual',
      'Failed to save column mapping'
    );

  const handleDeleteMapping = () =>
    run(
      () => deleteMarketplaceColumnMapping(store.storeId),
      'Column mapping deleted; this store re-resolves from Glue on its next file',
      'Failed to delete column mapping'
    );

  const handleSubmitStep = async (draft) => {
    const editing = stepEditor.step;
    const payload = {
      name: draft.name.trim(),
      sql: draft.sql.trim(),
      notes: draft.notes.trim(),
      enabled: draft.enabled,
    };

    const ok = await run(
      () =>
        editing
          ? updateMarketplaceTransformStep(editing.id, payload)
          : createMarketplaceTransformStep(store.storeId, payload),
      editing ? `Step "${payload.name}" updated` : `Step "${payload.name}" added`,
      editing ? 'Failed to update step' : 'Failed to add step'
    );
    if (ok) setStepEditor({ open: false, step: null });
  };

  const handleDeleteStep = async () => {
    const step = deletingStep;
    if (!step) return;
    const ok = await run(
      () => deleteMarketplaceTransformStep(step.id),
      `Step "${step.name}" deleted`,
      'Failed to delete step'
    );
    if (ok) setDeletingStep(null);
  };

  const handleToggleEnabled = (step, enabled) =>
    run(
      () => setMarketplaceTransformStepEnabled(step.id, enabled),
      `Step "${step.name}" ${enabled ? 'enabled' : 'disabled'}`,
      'Failed to toggle step'
    );

  const handleReorder = (stepIds) =>
    run(
      () => reorderMarketplaceTransformSteps(store.storeId, stepIds),
      'Step order updated',
      'Failed to reorder steps'
    );

  const handleSubmitSource = async (draft) => {
    const editing = sourceEditor.source;
    const payload = {
      name: draft.name.trim(),
      file_name: draft.fileName.trim(),
      shared: draft.shared,
      // null clears it back to "use the store's"; the API stores that as NULL.
      delimiter: draft.delimiter,
      pattern: draft.pattern.trim() || null,
    };
    // Trigger moves go through the dedicated endpoint so the clear-and-set is
    // one transaction. Only send it on create, where the API needs to know
    // whether a brand-new manifest's first row should trigger.
    if (!editing) payload.is_trigger = draft.isTrigger;

    const ok = await run(
      () =>
        editing
          ? updateMarketplaceSource(editing.id, payload)
          : createMarketplaceSource(store.storeId, payload),
      editing ? `Input "${payload.name}" updated` : `Input "${payload.name}" declared`,
      editing ? 'Failed to update input' : 'Failed to declare input'
    );
    if (ok) setSourceEditor({ open: false, source: null });
  };

  const handleDeleteSource = async () => {
    const source = deletingSource;
    if (!source) return;
    const ok = await run(
      () => deleteMarketplaceSource(source.id),
      `Input "${source.name}" deleted`,
      'Failed to delete input'
    );
    if (ok) setDeletingSource(null);
  };

  const handleSetTrigger = (source) =>
    run(
      () => setMarketplaceSourceTrigger(source.id),
      `"${source.name}" is now the trigger`,
      'Failed to set the trigger'
    );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
          Marketplace feed rules
        </h2>
        <p className="mt-1 text-slate-600 dark:text-slate-300">
          How store <span className="font-mono">{store.storeId}</span>&rsquo;s FTP inventory{' '}
          {multiFile ? 'files are' : 'file is'} read and transformed by the CDC pipeline.
        </p>
      </div>

      <ExecutionPathBanner
        path={store.executionPath}
        reason={store.executionPathReason}
        warnings={store.warnings}
        multiFile={multiFile}
      />

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
          Reading an imported store
        </h4>
        <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <p>
            • The <strong>column mapping</strong> holds header names only. It can look perfectly
            healthy while the Glue script&rsquo;s logic is gone.
          </p>
          <p>
            • The <strong>transform steps</strong> are where that logic lives. Check the importer
            notes on each before cutting the store over.
          </p>
        </div>
      </div>

      <ColumnMappingCard
        mapping={store.mapping}
        storeId={store.storeId}
        saving={busy}
        onSave={handleSaveMapping}
        onDelete={handleDeleteMapping}
      />

      <DeclaredInputsCard
        sources={store.sources}
        storeId={store.storeId}
        sourcesSupported={sourcesSupported}
        busy={busy}
        onAdd={() => setSourceEditor({ open: true, source: null })}
        onEdit={(source) => setSourceEditor({ open: true, source })}
        onDelete={(source) => setDeletingSource(source)}
        onSetTrigger={handleSetTrigger}
      />

      <TransformStepsList
        steps={store.steps}
        stepsSupported={stepsSupported}
        busy={busy}
        onAdd={() => setStepEditor({ open: true, step: null })}
        onEdit={(step) => setStepEditor({ open: true, step })}
        onDelete={(step) => setDeletingStep(step)}
        onToggleEnabled={handleToggleEnabled}
        onReorder={handleReorder}
      />

      <TransformTestPanel
        storeId={store.storeId}
        steps={store.steps}
        multiFile={multiFile}
        trigger={trigger}
      />

      <SourceEditorModal
        open={sourceEditor.open}
        source={sourceEditor.source}
        sources={store.sources}
        steps={store.steps}
        storeId={store.storeId}
        isFirstInput={store.sources.length === 0}
        saving={busy}
        onClose={() => setSourceEditor({ open: false, source: null })}
        onSubmit={handleSubmitSource}
      />

      <ModalBasic
        id="delete-declared-input"
        title="Delete declared input"
        modalOpen={!!deletingSource}
        setModalOpen={() => setDeletingSource(null)}
        width="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Delete input <span className="font-mono font-semibold">{deletingSource?.name}</span> (
            <span className="font-mono">{deletingSource?.fileName}</span>)?
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Refused if any step&rsquo;s SQL selects from it, enabled or not — a dangling reference
            fails every file. Also refused if it is the trigger while other inputs remain; move the
            trigger first.
          </p>
          {store.sources.length === 1 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This is the last input, so deleting it returns the store to the single-file path with
              its one upload registered as <code className="font-mono">source</code>.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeletingSource(null)}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSource}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete input'}
            </button>
          </div>
        </div>
      </ModalBasic>

      <StepEditorModal
        open={stepEditor.open}
        step={stepEditor.step}
        steps={store.steps}
        sources={store.sources}
        storeId={store.storeId}
        saving={busy}
        onClose={() => setStepEditor({ open: false, step: null })}
        onSubmit={handleSubmitStep}
      />

      <ModalBasic
        id="delete-transform-step"
        title="Delete transform step"
        modalOpen={!!deletingStep}
        setModalOpen={() => setDeletingStep(null)}
        width="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Delete step <span className="font-mono font-semibold">{deletingStep?.name}</span> (order{' '}
            {deletingStep?.stepOrder})?
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            If a later step selects from it by name the API will refuse — rewrite those steps first,
            or the chain fails every file for this store.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeletingStep(null)}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteStep}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete step'}
            </button>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default MarketplaceFeedRules;
